import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe2, CalendarDays, ChevronRight, Radio, Users, Eye, LogIn, Zap, Trophy, Swords, Clock, X, Crown } from "lucide-react";
import api from "@/lib/api";
import { CardRow } from "@/components/PlayCard";
import CommunityClips from "@/components/CommunityClips";
import { Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRoom } from "@/context/RoomContext";

const RAILS = [
  ["featured", "✨ Featured Games"],
  ["trending", "🔥 Trending Now"],
  ["recommended", "🎯 Recommended For You"],
  ["friends_playing", "🤝 Games Your Friends Are Playing"],
  ["live_multiplayer", "📡 Live Multiplayer Games"],
  ["new_releases", "🆕 New Releases"],
  ["editors_picks", "⭐ Editor's Picks"],
  ["top_rated", "🏆 Top Rated Games"],
  ["most_downloaded", "⬇️ Most Downloaded"],
  ["recently_updated", "🔄 Recently Updated"],
];

const QUICK = [
  { label: "🎮 All Games", to: "/app/play" },
  { label: "👾 Multiplayer", to: "/app/play?multiplayer=1" },
  { label: "🆓 Free", to: "/app/play?price=free" },
  { label: "💎 Premium", to: "/app/play?price=premium" },
  { label: "🏎️ Racing", to: "/app/play?genre=Racing" },
  { label: "🔫 Shooter", to: "/app/play?genre=Shooter" },
  { label: "🧩 Puzzle", to: "/app/play?genre=Puzzle" },
];

export default function CommunityHub() {
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [now, setNow] = useState(null);
  const { joinPublic } = useRoom();

  useEffect(() => { api.get("/hub").then((r) => setHub(r.data)).catch(() => setHub({})); }, []);
  useEffect(() => {
    const load = () => api.get("/hub/now-playing").then((r) => setNow(r.data)).catch(() => setNow({ live_rooms: [], recent_matches: [], stats: {} }));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const play = (g) => navigate(`/app/arcade/${g.id}`);
  const jumpIn = async (roomId, spectate = false) => { const r = await joinPublic(roomId, spectate); if (r) navigate("/app/multiplayer"); };

  return (
    <div className="p-5 lg:p-8 max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Globe2 className="w-8 h-8 text-[hsl(var(--primary))]" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Community Hub</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-5">Everything happening across GameView Studio — discover, play and connect.</p>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6" data-testid="hub-quick-filters">
        {QUICK.map((q) => (
          <button key={q.label} onClick={() => navigate(q.to)}
            className="px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors">
            {q.label}
          </button>
        ))}
      </div>

      <NowPlaying now={now} onJumpIn={jumpIn} onQuickMatch={() => navigate("/app/multiplayer")} onOpenGuilds={() => navigate("/app/guilds")} />

      <CommunityClips seeAllTo="/app/clips" />

      <HubGuildProPromo onOpenGuilds={() => navigate("/app/guilds")} />

      {!hub ? <Spinner /> : (
        <div data-testid="hub-rails">
          {RAILS.map(([key, title]) => (
            <CardRow key={key} title={title} games={hub[key]} onPlay={play} testid={`hub-rail-${key}`} />
          ))}

          {hub.events?.length > 0 && (
            <div className="mb-8" data-testid="hub-events">
              <h2 className="font-display text-xl font-bold mb-3 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[hsl(var(--primary))]" /> Community Events & Challenges</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {hub.events.map((e) => (
                  <button key={e.id} onClick={() => navigate("/app/arena")} data-testid={`hub-event-${e.id}`}
                    className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 card-hover">
                    <div className="text-2xl mb-1">{e.emoji || "🎉"}</div>
                    <div className="font-bold truncate">{e.title || e.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{e.description || "Join the challenge!"}</div>
                    <div className="text-xs text-[hsl(var(--primary))] font-semibold mt-2 flex items-center gap-1">View <ChevronRight className="w-3 h-3" /></div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MODE_LABEL = { coop: "Co-op", pvp: "PvP", party: "Party", open: "Open" };

function HubGuildProPromo({ onOpenGuilds }) {
  const [info, setInfo] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem("gv_hide_hub_guildpro") === "1");
  useEffect(() => { if (!hidden) api.get("/guilds/pro/info").then((r) => setInfo(r.data)).catch(() => {}); }, [hidden]);
  if (hidden || !info) return null;
  const dismiss = (e) => { e.stopPropagation(); localStorage.setItem("gv_hide_hub_guildpro", "1"); setHidden(true); };
  return (
    <button onClick={onOpenGuilds} data-testid="hub-guildpro-promo"
      className="relative w-full text-left overflow-hidden rounded-2xl mb-8 p-4 sm:p-5 bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-transparent border border-amber-400/30 card-hover">
      <span className="absolute -right-4 -top-5 text-[6rem] opacity-10 select-none pointer-events-none">👑</span>
      <span onClick={dismiss} className="absolute top-2.5 right-2.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="hub-guildpro-dismiss"><X className="w-4 h-4" /></span>
      <div className="flex items-center gap-3 relative">
        <Crown className="w-7 h-7 text-amber-500 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold">Make your guild legendary with Guild Pro</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Bigger rosters, custom look & featured Guild Wars — from {info.price_credits} credits.</p>
        </div>
        <ChevronRight className="w-5 h-5 text-amber-500 shrink-0" />
      </div>
    </button>
  );
}

const timeAgo = (iso) => {
  try {
    const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
};

const timeLeft = (iso) => {
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "ending…";
    const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000);
    if (d > 0) return `${d}d ${h}h`;
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  } catch { return ""; }
};

function NowPlaying({ now, onJumpIn, onQuickMatch, onOpenGuilds }) {
  if (!now) return null;
  const rooms = now.live_rooms || [];
  const recent = now.recent_matches || [];
  const wars = now.guild_wars || [];
  const stats = now.stats || {};
  if (rooms.length === 0 && recent.length === 0 && wars.length === 0) return null;

  return (
    <div className="mb-8" data-testid="hub-now-playing">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
          </span>
          Now Playing
          {stats.players_online > 0 && (
            <Badge variant="secondary" className="ml-1" data-testid="now-playing-stats">
              <Users className="w-3 h-3 mr-1" /> {stats.players_online} online · {stats.live_lobbies} live
            </Badge>
          )}
        </h2>
        <Button size="sm" onClick={onQuickMatch} className="rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7]" data-testid="now-playing-quickmatch">
          <Zap className="w-4 h-4" /> Quick Match
        </Button>
      </div>

      {rooms.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="now-playing-rooms">
          {rooms.map((r) => (
            <div key={r.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 card-hover" data-testid={`now-room-${r.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl">{r.game_cover}</span>
                <div className="min-w-0">
                  <div className="font-bold truncate">{r.game_title}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">Host: {r.host_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3 text-xs">
                <Badge variant="secondary">{MODE_LABEL[r.mode] || r.mode}</Badge>
                <Badge variant={r.status === "playing" ? "default" : "outline"}>{r.status === "playing" ? "In game" : "Lobby"}</Badge>
                <span className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] ml-auto"><Users className="w-3 h-3" /> {r.players}/{r.max_players}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full flex-1" onClick={() => onJumpIn(r.id, false)} data-testid={`now-join-${r.id}`}><LogIn className="w-3.5 h-3.5" /> Jump in</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => onJumpIn(r.id, true)} data-testid={`now-watch-${r.id}`}><Eye className="w-3.5 h-3.5" /> Watch</Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 text-sm text-[hsl(var(--muted-foreground))] flex items-center justify-between gap-3" data-testid="now-playing-empty">
          <span>No live lobbies right now — be the first to start a match!</span>
        </div>
      )}

      {recent.length > 0 && (
        <div className="mt-4" data-testid="now-playing-recent">
          <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Recent results</div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {recent.map((m, i) => (
              <div key={i} className="shrink-0 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 px-3 py-2 text-xs" data-testid={`now-recent-${i}`}>
                <div className="font-semibold flex items-center gap-1"><span>{m.game_cover}</span> <span className="truncate max-w-[9rem]">{m.game_title}</span></div>
                <div className="text-[hsl(var(--muted-foreground))] mt-0.5">{m.winner_avatar} {m.winner_name} won · {timeAgo(m.ended_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {wars.length > 0 && (
        <div className="mt-5" data-testid="guild-wars-strip">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Swords className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Guild Wars — live</div>
            <button onClick={onOpenGuilds} className="text-xs font-semibold text-[hsl(var(--primary))] hover:underline flex items-center gap-0.5">All guilds <ChevronRight className="w-3 h-3" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {wars.map((w) => {
              const cs = w.score?.challenger || 0, os = w.score?.opponent || 0;
              const total = cs + os, cPct = total ? (cs / total) * 100 : 50;
              return (
                <button key={w.id} onClick={onOpenGuilds} data-testid={`guild-war-${w.id}`}
                  className={`text-left rounded-xl border bg-[hsl(var(--card))] p-3 card-hover ${w.featured ? "border-amber-400/50" : "border-[hsl(var(--border))]"}`}>
                  {w.featured && <div className="text-[9px] font-extrabold text-amber-500 mb-1 flex items-center gap-1" data-testid={`war-featured-${w.id}`}>⭐ FEATURED — PRO GUILD</div>}
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-bold truncate flex items-center gap-1">{w.challenger.emoji} <span className="font-mono text-xs">{w.challenger.tag}</span></span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Clock className="w-3 h-3" /> {timeLeft(w.end_at)}</span>
                    <span className="font-bold truncate text-right flex items-center gap-1 justify-end"><span className="font-mono text-xs">{w.opponent.tag}</span> {w.opponent.emoji}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-extrabold tabular-nums text-[#3b9eff]" data-testid={`war-score-c-${w.id}`}>{cs}</span>
                    <div className="flex-1 h-2 rounded-full bg-red-500/25 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" style={{ width: `${cPct}%` }} />
                    </div>
                    <span className="font-display font-extrabold tabular-nums text-red-400" data-testid={`war-score-o-${w.id}`}>{os}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
